import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Layers, Zap, Phone } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Link } from "react-router-dom";
import { Reveal, Overline } from "@/components/Reveal";
import { PrimaryButton, OutlineButton } from "@/components/Buttons";
import { CTASection } from "@/components/CTASection";
import { GlowOrb, SectionLabel } from "@/components/Decor";
import { CountUp } from "@/components/CountUp";
import { IMG, SERVICES, WHY, STATS, PROCESS, TRUST_ITEMS, WHATSAPP_NUMBER } from "@/data/content";

export default function Home() {
  useSeo({
    title: "Imperium Group | Full-service marketingbureau — echt álles op het gebied van marketing",
    description: "Imperium Group is een full-service marketingbureau. Van digitale marketing, SEO en social media tot telemarketing, TV, radio, billboards en sportsponsoring — wij bieden echt álles op het gebied van marketing. Één partner voor al jouw campagnes.",
    path: "/",
  });

  const featured = SERVICES.slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-24 lg:pt-40 lg:pb-32">
        <div className="absolute inset-0 -z-20">
          <img src={IMG.heroOffice} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#090B0E] via-[#090B0E]/95 to-[#12151D]/70" />
        </div>
        <div className="absolute inset-0 -z-10 grid-bg" />
        <GlowOrb className="-left-32 top-10" color="flame" size={520} />
        <GlowOrb className="-right-40 top-40" color="mint" size={360} />

        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#FF4500]/30 bg-[#FF4500]/5 px-4 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#FF4500]">
              <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-[#FF4500]" /> Full-Service Marketingbureau
            </div>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl">
              Echt <span className="flame-text">álles</span> op het gebied van marketing.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
              Van digitale marketing, SEO en social media tot telemarketing, TV, radio, billboards en sportsponsoring. Imperium Group is jouw partner voor het complete marketingspectrum — alles onder één dak.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <PrimaryButton to="/offerte" testid="hero-offerte-btn">Offerte aanvragen</PrimaryButton>
              <OutlineButton to="/diensten" testid="hero-diensten-btn">Bekijk alle diensten</OutlineButton>
            </div>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" data-testid="hero-call-link" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-400 transition-colors hover:text-[#FF4500]">
              <Phone className="h-4 w-4" /> Liever direct sparren? Bel of app ons <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="relative">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 glow-flame">
              <img src={IMG.billboard} alt="Marketingcampagnes van Imperium Group" className="h-[500px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#090B0E] via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 rounded-2xl border border-[#FF4500]/30 bg-[#090B0E]/90 p-5 backdrop-blur-md float-slow">
              <div className="font-display text-2xl font-bold flame-text">360°</div>
              <div className="font-mono text-xs uppercase tracking-[0.15em] text-slate-400">marketing</div>
            </div>
            <div className="absolute -right-4 top-8 rounded-2xl border border-white/10 bg-[#090B0E]/90 p-4 backdrop-blur-md float-slow" style={{ animationDelay: "1.5s" }}>
              <div className="font-display text-lg font-bold text-white">Online & offline</div>
              <div className="text-xs text-slate-400">alle kanalen</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="relative border-y border-white/10 bg-[#12151D] py-6" aria-hidden>
        <div className="marquee-track gap-4">
          {[...TRUST_ITEMS, ...TRUST_ITEMS].map((t, i) => (
            <span key={i} className="mx-2 inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-slate-400">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF4500]" /> {t}
            </span>
          ))}
        </div>
      </section>

      {/* WAAROM IMPERIUM */}
      <section className="relative overflow-hidden py-24 lg:py-32" data-testid="waarom-section">
        <GlowOrb className="-left-40 top-1/3" color="flame" size={380} />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img src={IMG.meeting} alt="Marketingteam van Imperium Group" className="h-[440px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#090B0E] via-transparent to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <SectionLabel icon={Sparkles}>Waarom Imperium Group?</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white lg:text-4xl">Eén bureau voor jouw complete marketing.</h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              Geen losse bureaus meer die langs elkaar heen werken. Imperium Group combineert álle vormen van marketing — digitaal én traditioneel — tot één strategie met één aanspreekpunt.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {WHY.map((w) => (
                <div key={w.title} className="rounded-2xl border border-white/10 bg-[#12151D] p-5" data-testid={`why-${w.title.slice(0,6)}`}>
                  <h3 className="font-display text-base font-semibold text-white">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{w.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* DIENSTEN BENTO */}
      <section className="relative overflow-hidden border-y border-white/10 bg-[#12151D] py-24 lg:py-32" data-testid="diensten-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <SectionLabel icon={Layers}>Ons marketingspectrum</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white lg:text-4xl">Alle vormen van marketing, onder één dak</h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-400">Van online tot op straat, van beeld tot geluid — dit is slechts een greep uit wat wij leveren.</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.07}>
                  <Link to="/diensten" data-testid={`service-card-${s.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#090B0E] p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-[#FF4500]/50">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF4500]/10 text-[#FF4500]"><Icon className="h-6 w-6" /></span>
                    <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E599]">{s.tag}</div>
                    <h3 className="mt-2 font-display text-xl font-semibold text-white">{s.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#FF4500]">Meer weten <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-12 text-center"><PrimaryButton to="/diensten" testid="diensten-all-btn">Bekijk alle marketingdiensten</PrimaryButton></div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative overflow-hidden py-20" data-testid="stats-section">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-6 lg:grid-cols-4 lg:px-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="rounded-2xl border border-white/10 bg-[#12151D] p-7 text-center">
                <CountUp value={s.value} className="font-display text-4xl font-extrabold flame-text lg:text-5xl" />
                <div className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-400">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WERKWIJZE TEASER */}
      <section className="relative overflow-hidden border-t border-white/10 py-24 lg:py-32" data-testid="werkwijze-teaser">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Overline>Werkwijze</Overline>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white lg:text-4xl">Van audit tot schaalbare ROI</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((s, i) => (
              <Reveal key={s.n} delay={(i % 4) * 0.07}>
                <div className="h-full rounded-2xl border border-white/10 bg-[#12151D] p-7" data-testid={`step-${i}`}>
                  <span className="font-display text-3xl font-bold flame-text">{s.n}</span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
